import { Person } from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { UserPresenceResponseDtoStatusEnum } from "libs/cellera-sdk";
import { useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

type StyledBadgeProps = {
  badgeColor: string;
  enableRipple: boolean;
};

const StyledBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== "badgeColor" && prop !== "enableRipple",
})<StyledBadgeProps>(({ theme, badgeColor, enableRipple }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: badgeColor,
    color: badgeColor,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": enableRipple
      ? {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          animation: "ripple 1.2s infinite ease-in-out",
          border: "1px solid currentColor",
          content: '""',
        }
      : {
          content: "none",
        },
  },
  ...(enableRipple && {
    "@keyframes ripple": {
      "0%": {
        transform: "scale(.8)",
        opacity: 1,
      },
      "100%": {
        transform: "scale(2.4)",
        opacity: 0,
      },
    },
  }),
}));

type BadgeAvatarsProps = {
  photoUrl?: string;
  presenceStatus: UserPresenceResponseDtoStatusEnum;
};

export default function BadgeAvatar({
  photoUrl,
  presenceStatus,
}: BadgeAvatarsProps) {
  const { colors } = useCelleraTheme();
  const badgeConfig = useMemo(() => {
    const statusMap: Record<
      UserPresenceResponseDtoStatusEnum,
      StyledBadgeProps
    > = {
      [UserPresenceResponseDtoStatusEnum.Online]: {
        badgeColor: colors.success[600],
        enableRipple: true,
      },
      [UserPresenceResponseDtoStatusEnum.Offline]: {
        badgeColor: colors.border.dark,
        enableRipple: false,
      },
    };

    return (
      statusMap[presenceStatus] ??
      statusMap[UserPresenceResponseDtoStatusEnum.Offline]
    );
  }, [colors, presenceStatus]);

  return (
    <Stack direction="row" spacing={2}>
      <StyledBadge
        badgeColor={badgeConfig.badgeColor}
        enableRipple={badgeConfig.enableRipple}
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        variant="dot"
      >
        <Avatar
          sx={{
            bgcolor: colors.secondary.main,
          }}
          src={photoUrl}
        >
          <Person sx={{ color: colors.text.inverse }} />
        </Avatar>
      </StyledBadge>
    </Stack>
  );
}
